import React, 
{ useCallback, 
  useEffect, 
  useLayoutEffect, 
  useRef, 
  useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { IoMdClose } from "react-icons/io";


import EditInfo from "./pages/EditInfo";
import EditType from "./pages/EditType";
import EditTime from "./pages/EditTime";
import EditNotice from "./pages/EditNotice";
import EditPicture from "./pages/EditPicture";
import BranchStepper from "./BranchStepper";
import { CheckoutDetailsPerfectScrollbar } from "@components/CheckoutDetailsScrollbar";
import "@components/CheckoutDetailsScrollbar/checkout-details-scrollbar.scss";

import { 
    success, 
    error, 
    warning, 
    loading, 
    close, 
    isSwalVisible 
} from "@alert";
import * as api from '@apiMain';
import * as api_tags from '@API/api_tags';
import { 
    updateBranchInfo, 
    createBranch 
} from "@store/slice/user-slice";
import { 
    BRANCH_OPERATING_DAY_KEYS, 
    normalizeBranchOperatingHours 
} from "./branchOperatingHours";
import useFullscreenPanelSlide from "@hooks/useFullscreenPanelSlide";

const BRANCH_EDIT_PS_CLASS =
    "edit-warp-content-scroll checkout-details-ps--main checkout-details-ps--theme-slate-narrow scrollbar-container";

function createEmptyBranchForm() {
    return {
        branch_logo_url: null,
        branch_name: "",
        phone: "",
        branch_address: "",
        email: "",
        description: "",
        category: "",
        branch_operating_hours: normalizeBranchOperatingHours(null),
        branch_notice: '',
        branch_images: [],
        tagNames: [],
    };
}

function snapshotBranchForm(f) {
    const ho = {};
    BRANCH_OPERATING_DAY_KEYS.forEach((d) => {
        ho[d] = f.branch_operating_hours[d];
    });
    return JSON.stringify({
        branch_logo_url: f.branch_logo_url || null,
        branch_name: f.branch_name || "",
        phone: f.phone || "",
        branch_address: f.branch_address || "",
        email: f.email || "",
        description: f.description || "",
        category: f.category || "",
        branch_operating_hours: ho,
        branch_notice: f.branch_notice ?? "",
        branch_images: Array.isArray(f.branch_images) ? f.branch_images : [],
        tagNames: f.tagNames || [],
    });
}

const BranchInfo = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const token = useSelector((state) => state.user.user.token);
    const userInfo = useSelector((state) => state.user.user);
    const branchInfo = userInfo.brandInfo.branches.find((i) => i.id === parseInt(branchId));
    const [searchParams] = useSearchParams();
    const editParam = searchParams.get('edit') || '';
    const createParam = searchParams.get('create') || '';

    const [data, setData] = useState({
        "tagNames": [],
    });  // 預設 data 給後端
    const [form, setForm] = useState(() => ({
        "branch_logo_url": editParam ? branchInfo.branch_logo_url : null,
        "branch_name": editParam ? branchInfo.branch_name : "",
        "phone": editParam ? branchInfo.phone : "",
        "branch_address": editParam ? branchInfo.branch_address : "",
        "email": editParam ? branchInfo.email : "",
        "description": editParam ? branchInfo.description : "",
        "category": editParam ? branchInfo.category : "",
        "branch_operating_hours": normalizeBranchOperatingHours(
            editParam ? branchInfo.branch_operating_hours : null
        ),
        "branch_notice": editParam ? branchInfo.branch_notice : '',
        "branch_images": editParam && Array.isArray(branchInfo.branch_images) ? branchInfo.branch_images : [],
        "tagNames": editParam ? branchInfo.tagNames || [] : [],
    }));

    const initialFormSnapshotRef = useRef(snapshotBranchForm(form));
    const initialCreateFlowSnapshotRef = useRef(null);
    const formRef = useRef(form);
    const editInfoRef = useRef(null);
    const contentRef = useRef(null);
    const psRef = useRef(null);
    formRef.current = form;

    const { slideClassName, requestClose, isExiting } = useFullscreenPanelSlide({
        deferEnter: "raf",
    });

    /** 結束編輯／新增流程：先由右滑出再導頁 */
    const exitWithSlide = useCallback((url) => {
        requestClose(() => navigate(url));
    }, [requestClose, navigate]);

    useLayoutEffect(() => {
        initialFormSnapshotRef.current = snapshotBranchForm(formRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: snapshot on step/branch change only
    }, [createParam, editParam, branchId]);

    // 子元件 mount 正規化後再鎖 baseline，避免「未改動卻跳出離開警告」
    useEffect(() => {
        const timer = window.setTimeout(() => {
            initialFormSnapshotRef.current = snapshotBranchForm(formRef.current);
        }, 120);
        return () => window.clearTimeout(timer);
    }, [createParam, editParam, branchId]);

    useLayoutEffect(() => {
        if (!editParam && createParam) {
            if (!initialCreateFlowSnapshotRef.current) {
                initialCreateFlowSnapshotRef.current = snapshotBranchForm(createEmptyBranchForm());
            }
            return;
        }
        initialCreateFlowSnapshotRef.current = null;
    }, [editParam, createParam]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        psRef.current?.update();
    }, [createParam, editParam]);

    useEffect(() => {
        if (!editParam && createParam === 'type') {
            navigate('/options/branch?create=info', { replace: true });
        }
    }, [editParam, createParam, navigate]);

    const runBackNavigation = useCallback(() => {
        const backStepCreate = {
            'info': '',
            'time': 'info',
            'notice': 'time',
            'picture': 'notice',
            '': '/*'
        };
        const backStep = backStepCreate;
        // 編輯流程：返回一律離開面板（滑出）
        if (editParam) {
            exitWithSlide(`/options/branch/${branchId}`);
            return;
        }
        // 新增流程：第一步離開；其餘回上一步（面板不卸載，不滑出）
        if (createParam === 'info') {
            exitWithSlide('/options/branch');
            return;
        }
        navigate(`/options/branch?create=${backStep[createParam]}`);
    }, [navigate, branchId, editParam, createParam, exitWithSlide]);

    const runCancelCreateNavigation = useCallback(() => {
        exitWithSlide('/options/branch');
    }, [exitWithSlide]);

    const confirmIfDirty = useCallback((initialSnapshot, onConfirm) => {
        if (isExiting) return;
        if (!initialSnapshot || snapshotBranchForm(formRef.current) === initialSnapshot) {
            onConfirm();
            return;
        }
        warning(2,
            "確定離開？",
            "尚未儲存的變更將遺失",
            "確定",
            "取消",
            onConfirm
        );
    }, [isExiting]);

    const requestBack = useCallback(() => {
        confirmIfDirty(initialFormSnapshotRef.current, runBackNavigation);
    }, [confirmIfDirty, runBackNavigation]);

    const requestCancelCreate = useCallback(() => {
        confirmIfDirty(initialCreateFlowSnapshotRef.current, runCancelCreateNavigation);
    }, [confirmIfDirty, runCancelCreateNavigation]);

    const requestCancelEdit = useCallback(() => {
        confirmIfDirty(initialFormSnapshotRef.current, runBackNavigation);
    }, [confirmIfDirty, runBackNavigation]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (isSwalVisible()) return;
            if (isExiting) return;
            requestBack();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [requestBack, isExiting]);

    function renderText() {
        let text = '';
        switch (editParam || createParam) {
            case 'info':
                text = editParam ? '編輯分店基本資訊' : '新增分店基本資訊';
                break;
            case 'type':
                text = editParam ? '編輯分店類型' : '分店類型';
                break;
            case 'time':
                text = editParam ? '編輯營業時間' : '營業時間';
                break;
            case 'notice':
                text = editParam ? '編輯預約注意事項' : '預約注意事項';
                break;
            case 'picture':
                text = editParam ? '編輯分店相片' : '分店相片';
                break;
            default:
                text = '';
                break;
        }
        return text;
    }

    const handleNext = async () => {
        const type = editParam ? 'edit' : 'create';
        if (type === 'create') {
            const mergedData = { ...data, brand_id: userInfo.brandId };
            setData(mergedData);
            const nextStep = {
                'info': 'time',
                'time': 'notice',
                'notice': 'picture',
                'picture': '',
                '': '/*'
            };
            const url = editParam ? '' : `/options/branch${createParam !== 'picture' ? `?create=${nextStep[createParam]}` : ''}`;

            if (createParam === 'info') {
                const valid = await editInfoRef.current?.validateCreateInfo?.();
                if (!valid) return;
            } else if (!(mergedData.branch_logo_url && mergedData.branch_name && mergedData.phone && mergedData.branch_address && mergedData.email)) {
                return;
            }

            if (createParam === 'picture') {
                if (!mergedData.branch_images || mergedData.branch_images.length < 1) {
                    warning(1, 
                        "請至少上傳一張分店相片",
                        "", 
                        "我了解了"
                    );
                    return;
                }
                if (mergedData.brand_id) {
                    await postNewBranch(mergedData);
                }
            } else {
                navigate(url);
            }
        } else {
            if (editParam === 'info') {
                const valid = await editInfoRef.current?.validateEditInfo?.();
                if (!valid) return;
            }
            if (editParam === 'picture' && (!form.branch_images || form.branch_images.length < 1)) {
                warning(1, 
                    "請至少上傳一張分店相片",
                    "", 
                    "我了解了"
                );
                return;
            }
            if (Object.keys(data).length > 0) {
                const tagNames = data.tagNames;
                const hasTags = tagNames !== undefined;
                const newData = (({ tagNames: _tn, ...rest }) => rest)(data);
                const hasFieldUpdates = Object.keys(newData).length > 0;

                if (hasTags && hasFieldUpdates) {
                    const ok = await updateApi(newData, { navigateOnSuccess: false });
                    if (ok) {
                        await updateTag('branches', branchInfo.id, tagNames, data);
                    }
                } else if (hasTags) {
                    await updateTag('branches', branchInfo.id, tagNames, data);
                } else if (hasFieldUpdates) {
                    await updateApi(newData);
                } else {
                    exitWithSlide(`/options/branch/${branchId}`);
                }
            } else {
                exitWithSlide(`/options/branch/${branchId}`);
            }
        }
    };


    // API
    // 更新分店管理
    /**
     * @param {object} data
     * @param {{ navigateOnSuccess?: boolean }} [options] — 與 updateTag 接續呼叫時設 false，由最後一步導頁
     * @returns {Promise<boolean>}
     */
    const updateApi = async (data, options = {}) => {
        const { navigateOnSuccess = true } = options;
        try {
            loading('更新中', '請稍候...');
            const res = await api.updateBranch(token, branchId, data);
            close();
            if (res.data.status === 'success') {
                dispatch(updateBranchInfo({
                    branchId: branchId,
                    ...data
                }));
                if (navigateOnSuccess) {
                    success(0, '更新成功', '分店資訊已更新');
                    exitWithSlide(`/options/branch/${branchId}`);
                }
                return true;
            }
            close();
            error(1, 
                '更新失敗', 
                res.data?.msg || res.data?.message || '請稍後再試', 
                '確認'
            );
            return false;
        } catch (err) {
            close();
            console.log("更新分店管理失敗", err);
            error(1, "更新失敗", `${err.message}`, "確認");
            return false;
        }
    };
    // 轉換分店 logo Url
    const getUrlFromS3 = useCallback(async (file) => {
        try {
            const res = await api.convertFileToUrl(token, file);
            if (res.data.status === 'success') {
                setForm((data) => ({
                    ...data,
                    "branch_logo_url": res.data.data
                }));
                setData(prevData => ({
                    ...prevData,
                    branch_logo_url: res.data.data
                }));
            }
        } catch (err) {
            console.log("轉換分店 logo Url 失敗", err);
        }
    }, [token])
    // 轉換分店 Image List to Url
    const getListFromS3 = useCallback(async (files) => {
        try {
            const res = await api.convertArrayToUrlList(token, files);
            if (res.data.status === 'success') {
                setForm((prev) => ({
                    ...prev,
                    "branch_images": [...(Array.isArray(prev.branch_images) ? prev.branch_images : []), res.data.data]
                }));
                setData((prev) => ({
                    ...prev,
                    "branch_images": [...(Array.isArray(prev.branch_images) ? prev.branch_images : []), res.data.data]
                }));
            }
        } catch (err) {
            console.log("轉換分店 Image List to Url 失敗", err);
        }
    }, [token])
    // 創建分店
    const postNewBranch = async (data) => {
        try {
            loading("創建分店中", "請稍候...");
            const res = await api.createBranch(token, data);
            close();
            if (res.data.status === 'success') {
                dispatch(createBranch({
                    data: res.data.data[0]
                }));
                success(0, '創建成功', '分店已創建');
                exitWithSlide('/options/branch');
            } else {
                close();
                error(1, '創建失敗', res.data.msg || '請稍後重試');
            }
        } catch (err) {
            close();
            error(1, '創建失敗', err.msg || '請稍後重試');
        }
    };
    // 更新標籤
    /**
     * @param {{ navigateOnSuccess?: boolean }} [options] — 若前一步已 updateBranch 且將由此導頁，維持預設 true
     */
    const updateTag = async (entityType, entityId, tagName, data, options = {}) => {
        const { navigateOnSuccess = true } = options;
        try {
            loading('更新中', '請稍候...');
            const res = await api_tags.updateTag(token, entityType, entityId, tagName);
            close();
            if (res.data.status === 'success') {
                dispatch(updateBranchInfo({
                    branchId: branchId,
                    ...data
                }));
                if (navigateOnSuccess) {
                    success(0, '更新成功', '標籤已更新');
                    exitWithSlide(`/options/branch/${branchId}`);
                }
                return true;
            }
            close();
            error(1, 
                '更新失敗',
                res.data?.msg || res.data?.message || '請稍後再試', 
                '確認'
            );
            return false;
        } catch (err) {
            close();
            error(1, 
                '更新失敗', 
                err?.msg || err?.message || '請稍後再試', 
                '確認'
            );
            return false;
        }
    };

    if (!editParam && !createParam) return navigate('/*')

    const isCreateInfoStep = !editParam && createParam === 'info';
    const isInfoStep = editParam === 'info' || createParam === 'info';
    const isCreateFlow = !editParam && Boolean(createParam);
    const isEditFlow = Boolean(editParam);
    const showBranchNamePill =
      isEditFlow && ["time", "notice", "picture"].includes(editParam);
    const useSplitFooter = isCreateFlow || isEditFlow;
    const backButtonText = isCreateInfoStep
        ? '取消新增'
        : isCreateFlow
            ? '回上一步'
            : isEditFlow
                ? '取消編輯'
                : '返回';

    return (
        <div className={`edit edit--slide ${slideClassName}`.trim()}>
            <div className="edit-warp">
                <div
                    className={`edit-warp-top${isEditFlow ? ' edit-warp-top--shadow' : ''}`}
                    style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 72,
                        minHeight: 72,
                        lineHeight: "72px",
                        paddingLeft: 40,
                        paddingRight: 110,
                        boxSizing: "border-box",
                    }}
                >
                    <IoIosArrowBack
                        size={24}
                        className="back-icon"
                        onClick={() => requestBack()}
                        style={{
                            cursor: "pointer",
                            position: "absolute",
                            left: 16,
                            top: "50%",
                            transform: "translateY(-50%)",
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                requestBack();
                            }
                        }}
                        aria-label={backButtonText}
                    />
                    <div className="edit-warp-top__title-wrap">
                        <span className="edit-warp-top__title">{renderText()}</span>
                        {showBranchNamePill && branchInfo?.branch_name ? (
                            <span className="edit-warp-top__branch-pill">
                                {branchInfo.branch_name}
                            </span>
                        ) : null}
                    </div>
                    {(isCreateFlow || isEditFlow) && (
                        <button
                            type="button"
                            className="edit-warp-top__close"
                            onClick={() => (isCreateFlow ? requestCancelCreate() : requestCancelEdit())}
                            aria-label={isCreateFlow ? '取消新增' : '取消編輯'}
                        >
                            <IoMdClose size={24} />
                        </button>
                    )}
                </div>
                {!editParam && createParam && (
                    <div className="edit-warp-stepper">
                        <BranchStepper currentStep={createParam} />
                    </div>
                )}
                {isInfoStep ? (
                    <div className="edit-warp-content edit-warp-content--info-pane">
                        <EditInfo
                            ref={editInfoRef}
                            form={form}
                            setForm={setForm}
                            setData={setData}
                            getUrlFromS3={getUrlFromS3}
                            isCreateInfoStep={isCreateInfoStep}
                            scrollContainerRef={contentRef}
                            onPsSync={(ps) => { psRef.current = ps; }}
                        />
                    </div>
                ) : (
                    <CheckoutDetailsPerfectScrollbar
                        className={BRANCH_EDIT_PS_CLASS}
                        containerRef={(el) => { contentRef.current = el; }}
                        onSync={(ps) => { psRef.current = ps; }}
                    >
                        <div className="edit-warp-content edit-warp-content--step">
                        {(() => {
                            switch (editParam || createParam) {
                                case 'type':
                                    if (!editParam) return null;
                                    return <EditType
                                        form={form}
                                        setForm={setForm}
                                        setData={setData}
                                    />;
                                case 'time':
                                    return <EditTime
                                        form={form}
                                        setForm={setForm}
                                        setData={setData}
                                    />;
                                case 'notice':
                                    return <EditNotice
                                        form={form}
                                        setForm={setForm}
                                        setData={setData}
                                    />;
                                case 'picture':
                                    return <EditPicture
                                        form={form}
                                        setForm={setForm}
                                        setData={setData}
                                        getListFromS3={getListFromS3}
                                    />;
                                default:
                                    return null;
                            }
                        })()}
                        </div>
                    </CheckoutDetailsPerfectScrollbar>
                )}

                <div className={`edit-warp-down${useSplitFooter ? ' edit-warp-down--split' : ''}`}>
                    <button type="button" onClick={() => requestBack()}>
                        {backButtonText}
                    </button>
                    <button type="button" onClick={() => void handleNext()}>{editParam ? '保存' : '下一步'}</button>
                </div>
            </div>
        </div>
    )
};



export default BranchInfo;